/* eslint-disable @typescript-eslint/no-explicit-any */
import parse from "html-react-parser";
import React from "react";
import { useSelector } from "react-redux";

import { format } from "@gamedev-web/gamedev-sdk-core";

import { compareIdAzureApplicationStepStatus } from "../../helpers/idAzureStatusHelpers";
import { compareIdXboxApplicationStepStatus } from "../../helpers/idXboxStatusHelpers";
import { comparePitchApplicationStepStatus } from "../../helpers/pitchStatusHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import {
  IdAzureApplicationStep,
  IdXboxApplicationStep,
  PitchApplicationStep,
} from "../../models/status";
import {
  getCurrentGameConcept,
  getIdAzureApplicationStep,
  getIdXboxApplicationStep,
  getNdaSignedDate,
  getPitchApplicationStep,
} from "../../redux/selectors";
import { getOnboardingUser } from "../../redux/selectors/authSelectors";
import { convertDateToString } from "../../server/crm/api/mappers/utils/dateUtils";
import { ContentBoxIcon } from "../ContentBox";
import { addBoldTags, InfoBoxBase } from "../InfoBoxBase";

const InfoBoxNdaPreIntroduction = () => {
  const [infoBoxNDASignNonDisclosureAgreementTitle, infoBoxNDAFillIntro] = useStrings(
    "infoBoxNDASignNonDisclosureAgreementTitle",
    "infoBoxNDAFillIntro"
  );
  return (
    <InfoBoxBase
      title={infoBoxNDASignNonDisclosureAgreementTitle}
      icon={ContentBoxIcon.Lock}
      rightSideText={infoBoxNDAFillIntro}
    />
  );
};

const InfoBoxNdaIntroductionReady = () => {
  const [
    infoBoxNDASignNonDisclosureAgreementTitle,
    infoBoxNDADoubleCheckIntro,
    infoBoxNDASubmitIntroToComplete,
    infoBoxNDARegisteredEmail,
  ] = useStrings(
    "infoBoxNDASignNonDisclosureAgreementTitle",
    "infoBoxNDADoubleCheckIntro",
    "infoBoxNDASubmitIntroToComplete",
    "infoBoxNDARegisteredEmail"
  );
  const userInfo = useSelector(getOnboardingUser);
  const mainText = parse(
    format(
      infoBoxNDADoubleCheckIntro,
      userInfo && userInfo.emailAddress
        ? addBoldTags(userInfo.emailAddress)
        : addBoldTags(infoBoxNDARegisteredEmail)
    )
  );
  return (
    <InfoBoxBase
      title={infoBoxNDASignNonDisclosureAgreementTitle}
      icon={ContentBoxIcon.Lock}
      rightSideText={infoBoxNDASubmitIntroToComplete}
      mainText={mainText}
    />
  );
};

const InfoBoxNdaUnderReview = () => {
  const [
    infoBoxNDASignNonDisclosureAgreementTitle,
    infoBoxNDASubmitDate,
    infoBoxNDAUnderReview,
    infoBoxNDARegisteredEmail,
  ] = useStrings(
    "infoBoxNDASignNonDisclosureAgreementTitle",
    "infoBoxNDASubmitDate",
    "infoBoxNDAUnderReview",
    "infoBoxNDARegisteredEmail"
  );
  const userInfo = useSelector(getOnboardingUser);
  const submitDate = useSelector(getCurrentGameConcept)?.submittedDate;
  const rightSideTextNDA =
    submitDate && format(infoBoxNDASubmitDate, convertDateToString(submitDate));
  const mainText = format(
    infoBoxNDAUnderReview,
    userInfo?.emailAddress ?? infoBoxNDARegisteredEmail
  );
  return (
    <InfoBoxBase
      title={infoBoxNDASignNonDisclosureAgreementTitle}
      icon={ContentBoxIcon.Timer}
      rightSideText={rightSideTextNDA}
      mainText={mainText}
    />
  );
};

const InfoBoxNdaSent = () => {
  const [
    infoBoxNDASignNonDisclosureAgreementTitle,
    infoBoxNDASignNonDisclosureAgreement,
    infoBoxNDASignActionRequested,
  ] = useStrings(
    "infoBoxNDASignNonDisclosureAgreementTitle",
    "infoBoxNDASignNonDisclosureAgreement",
    "infoBoxNDASignActionRequested"
  );
  // TODO: Add docusign link
  const docusignLink = "";
  const mainText = parse(addBoldTags(infoBoxNDASignNonDisclosureAgreement));
  return (
    <InfoBoxBase
      title={infoBoxNDASignNonDisclosureAgreementTitle}
      icon={ContentBoxIcon.GreenCircle}
      mainText={mainText}
      actionRequiredText={infoBoxNDASignActionRequested}
      actionLink={docusignLink}
    />
  );
};

const InfoBoxNdaSigned = () => {
  const [infoBoxNDASignNonDisclosureAgreementTitle, infoBoxNDASignedDate] = useStrings(
    "infoBoxNDASignNonDisclosureAgreementTitle",
    "infoBoxNDASignedDate"
  );
  const ndaSignedDate = useSelector(getNdaSignedDate);
  const signedDate = ndaSignedDate ? convertDateToString(ndaSignedDate) : "";
  const rightSideTextNDA = signedDate && format(infoBoxNDASignedDate, signedDate);
  return (
    <InfoBoxBase
      title={infoBoxNDASignNonDisclosureAgreementTitle}
      icon={ContentBoxIcon.CheckMark}
      rightSideText={rightSideTextNDA}
    />
  );
};

export const IdXboxInfoBoxNDATracking = () => {
  const step = useSelector(getIdXboxApplicationStep);
  const compare = compareIdXboxApplicationStepStatus;

  if (compare(step, IdXboxApplicationStep.IntroductionReadyToSubmit) < 0) {
    /* before complete the introduction */
    return <InfoBoxNdaPreIntroduction />;
  }

  if (compare(step, IdXboxApplicationStep.IntroductionReadyToSubmit) === 0) {
    /* introduction complete but submit pending */
    return <InfoBoxNdaIntroductionReady />;
  }

  if (compare(step, IdXboxApplicationStep.NdaUnderReview) === 0) {
    /* introduction submit, DNA not sent */
    return <InfoBoxNdaUnderReview />;
  }

  if (compare(step, IdXboxApplicationStep.NdaSent) === 0) {
    /* NDA waiting to be sign */
    return <InfoBoxNdaSent />;
  }

  if (compare(step, IdXboxApplicationStep.NdaSent) > 0) {
    /* NDA signed */
    return <InfoBoxNdaSigned />;
  }

  /* Any other scenario */
  return null;
};

export const IdAzureInfoBoxNDATracking = () => {
  const step = useSelector(getIdAzureApplicationStep);
  const compare = compareIdAzureApplicationStepStatus;

  if (compare(step, IdAzureApplicationStep.IntroductionReadyToSubmit) < 0) {
    /* before complete the introduction */
    return <InfoBoxNdaPreIntroduction />;
  }

  if (compare(step, IdAzureApplicationStep.IntroductionReadyToSubmit) === 0) {
    /* introduction complete but submit pending */
    return <InfoBoxNdaIntroductionReady />;
  }

  if (compare(step, IdAzureApplicationStep.NdaUnderReview) === 0) {
    /* introduction submit, DNA not sent */
    return <InfoBoxNdaUnderReview />;
  }

  if (compare(step, IdAzureApplicationStep.NdaSent) === 0) {
    /* NDA waiting to be sign */
    return <InfoBoxNdaSent />;
  }

  if (compare(step, IdAzureApplicationStep.NdaSent) > 0) {
    /* NDA signed */
    return <InfoBoxNdaSigned />;
  }

  /* Any other scenario */
  return null;
};
export const PitchInfoBoxNDATracking = () => {
  const step = useSelector(getPitchApplicationStep);
  const compare = comparePitchApplicationStepStatus;

  if (compare(step, PitchApplicationStep.NdaUnderReview) === 0) {
    /* introduction submit, DNA not sent */
    return <InfoBoxNdaUnderReview />;
  }

  if (compare(step, PitchApplicationStep.NdaSent) === 0) {
    /* NDA waiting to be sign */
    return <InfoBoxNdaSent />;
  }

  if (compare(step, PitchApplicationStep.NdaSent) > 0) {
    /* NDA signed */
    return <InfoBoxNdaSigned />;
  }

  /* Any other scenario */
  return null;
};
